/**
 * AmericanSurgicalHospitalDemoPage
 * --------------------------------
 * Single-screen hospital pitch deck for SterileMate's Authorized-Distributor
 * relationship with American Surgical Company. Built to be presented
 * live (laptop/projector) and also shared as a link.
 *
 * Sections:
 *   1. Hero — Authorized Distributor + 2025 Neuro Patty shortage context
 *   2. What SterileMate has done during the shortage
 *   3. Q3 prime opportunity + commitment to bridge supplies
 *   4. Representative product showcase with hospital pricing
 *   5. Forecast capture form (saves to /api/order-request as a forecast lead)
 *
 * Route: /demo/american-surgical
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SiteHeader from "@/components/SiteHeader";
import {
  ShieldCheck, Award, Clock, Truck, MapPin, AlertTriangle, CheckCircle2,
  Send, Loader2, Phone, Mail, Calendar, ArrowRight, Package, FileText,
  TrendingUp, Building2, Stethoscope,
} from "lucide-react";
import { getAscPrice, refreshAscPricing } from "@/data/americanSurgicalPricing";

const API = process.env.REACT_APP_BACKEND_URL;

// Curated representative SKUs (line + size are display metadata). The
// `hosp` (retail) price is looked up at render time from the Mongo-backed
// admin catalog via getAscPrice(sku), so uploading a new prices CSV in
// Admin > Marketplace instantly updates what hospitals see here — no code
// change or redeploy required.
const SHOWCASE_PRODUCTS = [
  { sku: "60-02",  line: "Cottonoid Neuro Patties (Type 60)",    size: "¼ x ¼",        hot: true },
  { sku: "60-08",  line: "Cottonoid Neuro Patties (Type 60)",    size: "½ x 1½",       hot: true },
  { sku: "60-13",  line: "Cottonoid Neuro Patties (Type 60)",    size: "1 x 3" },
  { sku: "60-15",  line: "Cottonoid Neuro Patties (Type 60)",    size: "3 x 3" },
  { sku: "61-04",  line: "Cottonoid Strips w/ String (Type 61)", size: "1 x 6",        hot: true },
  { sku: "61-04S", line: "Cottonoid Strips Strung (Type 61S)",   size: "1 x 6 Strung" },
  { sku: "67-08",  line: "Premium Cottonoid (Type 67)",          size: "½ x 1½" },
  { sku: "67-09",  line: "Premium Cottonoid (Type 67)",          size: "½ x 3" },
  { sku: "63-08",  line: "X-Ray Detectable Patties (Type 63)",   size: "½ x 2",        hot: true },
  { sku: "80-09",  line: "Cottonoid Pledgets (Type 80)",         size: "½ x 3" },
];

// Default forecast row — admin can add more
const NEW_FORECAST_ROW = () => ({ sku: "", size: "", q3_units: "", q4_units: "" });

// "What we've done" advocacy timeline during the 2025 shortage.
const TIMELINE = [
  { tag: "Q1 2025", title: "Identified the shortage early", body: "Flagged the national Cottonoid backorder while many distributors were still routing it to the gray market." },
  { tag: "Q2 2025", title: "Stood up an emergency sourcing desk", body: "Routed 100+ neuro-patty RFQs across the country, secured spot allotments for partner hospitals during peak constraint." },
  { tag: "Q3 2025", title: "Direct relationship secured", body: "Negotiated authorized-distributor terms with American Surgical Company — non-GPO, manufacturer-direct pricing." },
  { tag: "Q4 2025", title: "First allotment scheduling", body: "Onboarding hospital forecasts now to reserve Q3 / Q4 2026 production capacity." },
];

export default function AmericanSurgicalHospitalDemoPage() {
  const [contact, setContact] = useState({
    hospital: "", contact_name: "", title: "", email: "", phone: "", city: "", state: "", zip: "",
  });
  const [rows, setRows] = useState([NEW_FORECAST_ROW(), NEW_FORECAST_ROW(), NEW_FORECAST_ROW()]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Bump on price refresh to force re-render of the pricing table.
  const [, setPriceTick] = useState(0);
  // Hospital-aware showcase: when the URL has ?hospital=X (or ?email=X), we
  // look up the last few SKUs that hospital has ordered from any Sterile
  // Mate flow (backorders + orders) and surface them at the top of the
  // pricing table. Falls back to the curated 10 SHOWCASE_PRODUCTS below.
  const [personalizedSkus, setPersonalizedSkus] = useState([]);
  const [personalizedHospital, setPersonalizedHospital] = useState("");
  useEffect(() => {
    refreshAscPricing().then((r) => { if (r?.ok) setPriceTick((n) => n + 1); });
    const params = new URLSearchParams(window.location.search);
    const hospital = params.get("hospital") || "";
    const email = params.get("email") || "";
    if (!hospital && !email) return;
    const qs = new URLSearchParams();
    if (hospital) qs.set("hospital", hospital);
    if (email) qs.set("email", email);
    fetch(`${API}/api/american-surgical/hospital-history?${qs.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.skus?.length) {
          setPersonalizedSkus(data.skus);
          setPersonalizedHospital(data.hospital || hospital);
        }
      })
      .catch(() => {});
  }, []);

  // Merged view: personalized rows first (looked up in the live pricing
  // map for description + size), then any curated SHOWCASE_PRODUCTS that
  // aren't already in the personalized list.
  const displayRows = (() => {
    const out = [];
    const seen = new Set();
    for (const sku of personalizedSkus) {
      const live = getAscPrice(sku);
      if (!live) continue;
      out.push({
        sku,
        line: live.pack ? `${live.size} · ${live.pack.slice(0, 40)}` : (live.size || sku),
        size: live.size || "",
        personalized: true,
      });
      seen.add(sku);
    }
    for (const p of SHOWCASE_PRODUCTS) {
      if (seen.has(p.sku)) continue;
      out.push({ ...p, personalized: false });
    }
    return out;
  })();

  const setRow = (i, k, v) =>
    setRows((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((p) => [...p, NEW_FORECAST_ROW()]);

  const submit = async (e) => {
    e.preventDefault();
    const valid = rows.filter((r) => r.sku.trim() || r.size.trim());
    if (!contact.hospital.trim() || !contact.email.trim()) {
      toast.error("Hospital name and contact email are required");
      return;
    }
    if (valid.length === 0) {
      toast.error("Add at least one forecast line");
      return;
    }
    setSubmitting(true);
    try {
      const shipAddr = [contact.city, contact.state, contact.zip].filter(Boolean).join(", ");
      const items = valid.map((r) => ({
        name: `${r.sku || "(no SKU)"} — ${r.size || "(no size)"}`,
        quantity: parseInt(r.q3_units || "0", 10) + parseInt(r.q4_units || "0", 10) || 1,
        notes: `Q3: ${r.q3_units || 0} · Q4: ${r.q4_units || 0}`,
      }));
      await axios.post(`${API}/api/order-request`, {
        order_type: "forecast",
        hospital_name: contact.hospital,
        address: shipAddr,
        contact_name: contact.contact_name || "Procurement",
        contact_email: contact.email,
        contact_phone: contact.phone || "",
        item_name: "American Surgical Q3/Q4 Forecast",
        quantity: items.reduce((s, it) => s + it.quantity, 0),
        notes: `[FORECAST] ${notes}`,
        items,
        shipping_address: shipAddr,
        use_hospital_courier: false,
        ship_via: "Best Way",
        terms: "Net 30",
      });
      setSubmitted(true);
      toast.success("Forecast submitted — we'll reserve allotment for your facility.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-amber-50/40">
      <SiteHeader />

      {/* ============================================================== HERO */}
      <header className="relative overflow-hidden bg-[#0b1d3a] text-white">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.pexels.com/photos/4225880/pexels-photo-4225880.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=1400"
               alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b1d3a]/95 via-[#0b1d3a]/85 to-[#0b1d3a]/65" />
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-400/40 text-amber-300 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5">
            <Award className="w-3.5 h-3.5" /> Authorized Distributor · American Surgical Company
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-5 max-w-4xl">
            Your Neuro-Patty supply chain — <span className="text-amber-400">unbroken</span> through Q3 &amp; beyond.
          </h1>
          <p className="text-lg text-slate-200 max-w-3xl leading-relaxed mb-7">
            In the wake of the 2025 Cottonoid / Neuro patty shortage, Sterile Mate has secured an authorized-distributor relationship with American Surgical Company. Starting <strong className="text-white">Q3 2026</strong>, we&apos;re positioned to be your <strong className="text-amber-300">prime supplier</strong> — manufacturer-direct, non-GPO, with reserved allotment for the hospitals that forecast with us today.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a href="#forecast" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-[#0b1d3a] font-bold px-6 py-3 rounded-lg transition-colors shadow-lg">
              Reserve Your Q3 / Q4 Allotment <ArrowRight className="w-4 h-4" />
            </a>
            <a href={`${API}/api/downloads/american-surgical-demo`} className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-6 py-3 rounded-lg transition-colors shadow-lg" data-testid="demo-pdf-download">
              Download PDF <FileText className="w-4 h-4" />
            </a>
            <a href="/marketplace/american-surgical" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-6 py-3 rounded-lg transition-colors backdrop-blur-sm">
              View full catalog <FileText className="w-4 h-4" />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-10 max-w-3xl">
            {[
              { v: "100+", l: "RFQs routed during shortage" },
              { v: "Q3 '26", l: "First allotment delivery" },
              { v: "Non-GPO", l: "Manufacturer-direct pricing" },
              { v: "1-Day", l: "Quote response time" },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3.5">
                <div className="text-2xl font-extrabold text-amber-400">{s.v}</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-300 mt-1 font-semibold">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* ======================================================= THE SHORTAGE */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-amber-700 text-xs font-bold uppercase tracking-widest mb-3">
              <AlertTriangle className="w-4 h-4" /> The 2025 Neuro Patty Crisis
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 leading-tight">
              Hospitals canceled cases. We answered the phone.
            </h2>
            <p className="text-slate-700 mb-3 leading-relaxed">
              When the national Cottonoid / Neuro patty shortage hit OR schedules in 2025, prime distributors quietly diverted product to the highest bidder. Surgical teams scrambled. Some hospitals lost <strong>days of neurosurgery capacity</strong>.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Sterile Mate ran an emergency sourcing desk across the country — pulling allotments from secondary channels, manufacturer rejects, and direct mill relationships — to keep patient care on schedule for the hospitals that called us.
            </p>
          </div>
          <div className="bg-slate-900 text-white rounded-2xl p-6 shadow-xl">
            <p className="text-amber-400 font-bold uppercase tracking-widest text-xs mb-3">During the shortage we…</p>
            <ul className="space-y-3 text-sm">
              {[
                "Routed 100+ neuro-patty RFQs in 90 days",
                "Maintained 1-day quote response time when primes ran 5-7 days",
                "Sourced 14 hospitals through their Q2 capacity gap",
                "Held line pricing instead of riding the spot-market spike",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ======================================================== TIMELINE */}
      <section className="bg-slate-50 border-y border-slate-200 py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-amber-700 text-xs font-bold uppercase tracking-widest mb-2">The path to Authorized Distributor</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900">What Sterile Mate has done</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {TIMELINE.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm relative">
                <div className="absolute -top-3 left-5 bg-[#0b1d3a] text-amber-300 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">{t.tag}</div>
                <h3 className="text-base font-bold text-slate-900 mt-2 mb-2">{t.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================== Q3 COMMITMENT BANNER */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div className="rounded-3xl bg-gradient-to-br from-[#0b1d3a] via-[#0b1d3a] to-[#1e3a8a] text-white p-8 lg:p-10 shadow-xl">
          <div className="grid lg:grid-cols-3 gap-6 items-center">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-400/40 text-amber-300 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest mb-4">
                <Calendar className="w-3.5 h-3.5" /> Q3 2026 commitment
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight mb-3">
                We keep sourcing until <span className="text-amber-300">your first allotment ships.</span>
              </h2>
              <p className="text-slate-300 leading-relaxed">
                Between today and your Q3 allotment delivery, Sterile Mate will continue actively sourcing American Surgical product through every channel available — so your OR doesn&apos;t lose a single case. The moment your reserved allotment arrives, we transition you to <strong className="text-white">manufacturer-direct pricing</strong> with the predictable supply your team deserves.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5">
              <p className="text-amber-400 font-bold uppercase tracking-widest text-[10px] mb-3">What we need from you</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">1.</span> Your Q3 + Q4 2026 forecast (SKUs &amp; quantities)</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">2.</span> Ship-to facility info</li>
                <li className="flex items-start gap-2"><span className="text-amber-400 font-bold">3.</span> 10 minutes to confirm pricing</li>
              </ul>
              <p className="text-[11px] text-slate-400 mt-3">We reserve allotment on a first-come basis once American Surgical confirms production scheduling.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================ PRODUCT SHOWCASE */}
      <section className="bg-slate-50 border-y border-slate-200 py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
            <div>
              <p className="text-amber-700 text-xs font-bold uppercase tracking-widest mb-2">Most-requested SKUs</p>
              <h2 className="text-3xl font-extrabold text-slate-900">Representative pricing — 2026 list</h2>
              <p className="text-sm text-slate-500 mt-1">Hospital pricing shown for budgeting; final pricing confirmed at PO. Full catalog &gt;200 SKUs.</p>
            </div>
            <a href="/marketplace/american-surgical" className="text-sm font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1">
              Browse full catalog <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0b1d3a] text-amber-300 text-[10px] uppercase tracking-widest font-bold">
                  <tr>
                    <th className="px-4 py-3 text-left">SKU</th>
                    <th className="px-4 py-3 text-left">Product line</th>
                    <th className="px-4 py-3 text-left">Size</th>
                    <th className="px-4 py-3 text-right">2026 Price ($/box)</th>
                    <th className="px-4 py-3 text-center">Pack</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayRows.map((p) => {
                    const live = getAscPrice(p.sku);
                    const price = live?.hosp;
                    const pack = live?.pack || "20 pouches × 10";
                    return (
                      <tr key={p.sku} className={p.personalized ? "bg-amber-50/60 hover:bg-amber-100/60" : "hover:bg-amber-50/30"}>
                        <td className="px-4 py-2.5 font-mono font-bold text-slate-900">
                          {p.sku}
                          {p.hot && <span className="ml-2 text-[9px] uppercase tracking-widest bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold">High demand</span>}
                          {p.personalized && <span className="ml-2 text-[9px] uppercase tracking-widest bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold" data-testid={`hosp-personalized-${p.sku}`}>Your usual</span>}
                        </td>
                        <td className="px-4 py-2.5 text-slate-700">{p.line}</td>
                        <td className="px-4 py-2.5 text-slate-700">{p.size}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-emerald-700" data-testid={`hosp-price-${p.sku}`}>
                          {price != null ? `$${price.toFixed(2)}` : <span className="text-slate-400 font-normal italic">Call for pricing</span>}
                        </td>
                        <td className="px-4 py-2.5 text-center text-xs text-slate-500">{pack}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-900 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span><strong>Authorized distributor pricing</strong> is non-GPO, manufacturer-direct. Your final delivered price is held at the rate above through your Q3 &amp; Q4 2026 allotment.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================ FORECAST FORM */}
      <section id="forecast" className="max-w-7xl mx-auto px-6 py-14">
        <div className="text-center mb-8">
          <p className="text-amber-700 text-xs font-bold uppercase tracking-widest mb-2">Reserve your allotment</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-3">Submit your Q3 / Q4 2026 forecast</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Give us your expected volume by SKU. We&apos;ll lock in your hospital pricing and reserve manufacturer capacity. <strong>No commitment until you approve the final allotment.</strong>
          </p>
        </div>

        {submitted ? (
          <div className="max-w-2xl mx-auto bg-white border border-emerald-200 rounded-2xl p-8 text-center shadow-md">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Forecast received — thank you!</h3>
            <p className="text-slate-600 mb-3">
              George Broughton or your assigned Sterile Mate rep will follow up within one business day to confirm hospital pricing and reserve your Q3 / Q4 allotment.
            </p>
            <p className="text-sm text-slate-500">
              Questions in the meantime: <a href="mailto:orders@sterilemate.com" className="text-amber-700 font-semibold">orders@sterilemate.com</a> · <a href="tel:2814563458" className="text-amber-700 font-semibold">(281) 456-3458</a>
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 shadow-md">
            {/* Hospital info */}
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> Your hospital
            </h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <Input required placeholder="Hospital / facility name *" value={contact.hospital} onChange={(e) => setContact({ ...contact, hospital: e.target.value })} data-testid="fc-hospital" />
              <Input required type="email" placeholder="Procurement email *" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="fc-email" />
              <Input placeholder="Your name" value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} />
              <Input placeholder="Title (e.g. OR Materials Manager)" value={contact.title} onChange={(e) => setContact({ ...contact, title: e.target.value })} />
              <Input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              <div className="grid grid-cols-12 gap-2">
                <Input placeholder="City" className="col-span-6" value={contact.city} onChange={(e) => setContact({ ...contact, city: e.target.value })} />
                <Input placeholder="ST" maxLength={2} className="col-span-2 uppercase" value={contact.state} onChange={(e) => setContact({ ...contact, state: e.target.value.toUpperCase().slice(0, 2) })} />
                <Input placeholder="ZIP" className="col-span-4" value={contact.zip} onChange={(e) => setContact({ ...contact, zip: e.target.value })} />
              </div>
            </div>

            {/* Forecast lines */}
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 mt-2 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> Forecast (boxes / quarter)
            </h3>
            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 px-1">
                <span className="col-span-3">SKU</span>
                <span className="col-span-4">Size</span>
                <span className="col-span-2 text-center">Q3 boxes</span>
                <span className="col-span-2 text-center">Q4 boxes</span>
              </div>
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <Input placeholder="e.g. 60-08" className="col-span-3" value={r.sku} onChange={(e) => setRow(i, "sku", e.target.value)} data-testid={`fc-sku-${i}`} />
                  <Input placeholder="e.g. ½ x 1½" className="col-span-4" value={r.size} onChange={(e) => setRow(i, "size", e.target.value)} />
                  <Input type="number" min="0" placeholder="0" className="col-span-2" value={r.q3_units} onChange={(e) => setRow(i, "q3_units", e.target.value)} />
                  <Input type="number" min="0" placeholder="0" className="col-span-2" value={r.q4_units} onChange={(e) => setRow(i, "q4_units", e.target.value)} />
                </div>
              ))}
              <button type="button" onClick={addRow} className="text-xs font-semibold text-amber-700 hover:text-amber-800">+ Add another SKU</button>
            </div>

            <Textarea
              className="mt-4"
              rows={3}
              placeholder="Notes — preferred ship cadence, current PAR levels, anything else we should know"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-5 pt-5 border-t border-slate-200">
              <p className="text-xs text-slate-500">Submitting reserves your allotment — no PO yet. We&apos;ll follow up to confirm pricing.</p>
              <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700 gap-1.5 px-6">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? "Submitting…" : "Submit Forecast"}
              </Button>
            </div>
          </form>
        )}
      </section>

      {/* ================================================== CONTACT FOOTER */}
      <footer className="bg-[#0b1d3a] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-6 items-center">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-amber-500 text-[#0b1d3a] flex items-center justify-center font-black text-lg">AI</div>
            <div>
              <p className="font-bold">Sterile Mate LLC</p>
              <p className="text-xs text-amber-300">Authorized Distributor · American Surgical Company</p>
            </div>
          </div>
          <div className="text-sm space-y-1">
            <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-amber-400" /> <a href="mailto:george.broughton@sterilemate.com" className="hover:text-amber-300">george.broughton@sterilemate.com</a></p>
            <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-amber-400" /> <a href="tel:2814563458" className="hover:text-amber-300">(281) 456-3458</a></p>
            <p className="flex items-center gap-2"><Stethoscope className="w-3.5 h-3.5 text-amber-400" /> George Ivory Broughton III · CEO &amp; President</p>
          </div>
          <div className="text-xs text-slate-400 sm:text-right">
            <p>sterilemate.com · sterilemate.com</p>
            <p>This is an information sheet. Authorized-distributor agreement signed with American Surgical Company; manufacturer-direct pricing effective Q3 2026.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
